import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { parseInlineMarkdown } from '@orborix/shared-types';
import { BrushStroke, Waveform } from '../../../components/doodles/Doodles.js';
import { speakTextWithAlignment, translateSentence } from '../../../lib/englishApi.js';
import { fetchTurnAudioBlob } from '../../../lib/sessionsApi.js';
import { useAudioBlob } from '../../../lib/audioBlobPlayer.js';
import { charIndexAt, replayAlignment } from '../replayKaraoke.js';
import { SpokenBody, type SpokenChallengeBlock } from './SpokenBody.js';
import { locateChallengeBlock, type GameChallengeTag } from '../gameChallenge.js';
import { GAME_ROUND2_COPY, translateItColorStyle } from './GameRound2.js';
import { useBubbleWordLookup } from './useBubbleWordLookup.js';
import { EscucharBtn, TranslateBubbleBtn } from './bubbleActions.js';
import type { AiContentReportSurface } from '@orborix/shared-types';
import { ReportAiReply } from '../../../components/ai-report/ReportAiReply.js';

/**
 * Getter estable para la burbuja cerrada: el texto ya está entero, así que el
 * karaoke del replay sólo mueve el resaltado (nunca el revelado). Definido a
 * nivel de módulo para no romper la memoización aguas abajo ni re-suscribir el
 * rAF de `useKaraoke` en cada render.
 */
const FULL_REVEAL = (): number => Infinity;

/**
 * The translate-it challenge this reply served (plan 47 Round 2): shown ONCE,
 * as a highlighted block inside the bubble. `head` is the block's head row
 * («Ronda N», «No sé»), built by the caller.
 */
export interface CoachGameChallenge {
  tag: GameChallengeTag;
  head: ReactNode;
}

interface CoachCardProps {
  text: string;
  /** Burbuja viva (streaming) → reveal/karaoke desde los getters. */
  streaming: boolean;
  showBrush: 'short' | 'long' | false;
  audioOff: boolean;
  /** Traducción global (si el toggle está on). */
  translation?: string | null;
  /** Si la traducción global está activa (oculta el botón por-burbuja). */
  translationEnabled: boolean;
  animate: boolean;
  getRenderEndIdx: () => number;
  getCharIndex: () => number;
  /**
   * Karaoke del REPLAY: tiempos (ms, relativos al inicio del audio del turno)
   * de cada carácter de `text`, congelados cuando la burbuja se cerró. Con
   * esto, volver a apretar play vuelve a marcar palabra por palabra en vez de
   * reproducir el audio sobre texto inerte. Ausente → si hay que re-sintetizar
   * el alignment se pide en la misma llamada; si suena el audio grabado sin
   * snapshot (p. ej. modo realtime), el replay va sin resaltado.
   */
  alignment?: ReadonlyArray<number>;
  sessionId?: string;
  /**
   * Índice de turno (1-based) para reproducir el audio REAL persistido del
   * coach (RECORD_SESSION_AUDIO) en vez de re-sintetizar TTS. Sólo para
   * burbujas no-streaming de la sesión viva. Si falta o el audio no existe,
   * cae a `speakText`.
   */
  replayTurnIndex?: number;
  /** Interrumpe al coach mientras habla (solo en la burbuja viva con voz). */
  onInterrupt?: () => void;
  /**
   * Burbuja del coach que sigue a OTRA burbuja del coach: en densidad
   * compacta el header (waveform + COACH) se suprime vía CSS — la posición y
   * el color ya codifican el emisor.
   */
  consecutive?: boolean;
  /**
   * Charla asistida: la última línea del coach va grande y sin recuadro, como
   * en el repaso (Emmanuel eligió la maqueta 1 · Escenario, 16/10).
   */
  featured?: boolean;
  /** The translate-it challenge this reply served, drawn as a block (Round 2). */
  gameChallenge?: CoachGameChallenge | undefined;
  /**
   * Where the "report this reply" flag says the reply was shown (store review:
   * every closed coach bubble can be reported). Default: the live conversation.
   */
  reportSurface?: AiContentReportSurface;
  /** Turn of the reply for the report, when it has no recorded audio to replay. */
  reportTurnIndex?: number;
}

export function CoachCard({
  text,
  streaming,
  showBrush,
  audioOff,
  translation,
  translationEnabled,
  animate,
  getRenderEndIdx,
  getCharIndex,
  alignment,
  sessionId,
  replayTurnIndex,
  onInterrupt,
  consecutive = false,
  featured = false,
  gameChallenge,
  reportSurface = 'conversation',
  reportTurnIndex,
}: CoachCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const tappable = !streaming;

  // El `text` del coach puede traer markdown (`**on**`, `*nice*`). Lo limpiamos
  // UNA vez acá y usamos el texto limpio para TODO (render, karaoke, selección
  // de palabras, traducción y replay del TTS) — así el TTS nunca recibe
  // markdown y el karaoke (que mapea el alignment de ElevenLabs por índice de
  // carácter) queda alineado con lo que se muestra. La negrita entra como capa
  // de formato (rangos), no como caracteres. Misma util que el backend usa para
  // limpiar el TTS, así el `clean` coincide byte a byte. Ver
  // `@orborix/shared-types` markdown-speech.ts.
  const { clean, bold } = useMemo(() => parseInlineMarkdown(text), [text]);

  // The challenge block (plan 47 Round 2), placed in `clean` coordinates — the
  // same string the karaoke and the word taps index. Not found in the text →
  // a block under it (no karaoke there).
  const challengeTag = gameChallenge?.tag;
  const placement = useMemo(
    () => (challengeTag ? locateChallengeBlock(text, challengeTag) : null),
    [text, challengeTag],
  );
  const challengeBlock: SpokenChallengeBlock | null =
    gameChallenge && placement?.kind === 'inline'
      ? {
          spanStart: placement.spanStart,
          start: placement.start,
          end: placement.end,
          spanEnd: placement.spanEnd,
          head: gameChallenge.head,
          ariaLabel: GAME_ROUND2_COPY.blockAria(gameChallenge.tag.round),
          style: translateItColorStyle(),
        }
      : null;

  // Alignment que gobierna el karaoke del replay EN CURSO. Es un ref (no
  // estado) porque lo muestrea el rAF de `useKaraoke`: se fija dentro del
  // fetcher, antes de que el player pase a 'playing', así el primer frame ya
  // tiene con qué marcar.
  const replayAlignRef = useRef<ReadonlyArray<number> | null>(null);

  // Replay de la burbuja: si tenemos turno + sesión, reproducimos el audio
  // REAL grabado (flash, lo que sonó en vivo) — el snapshot del alignment cae
  // exacto encima porque describe ESE audio. Si no existe (404, p.ej.
  // RECORD_SESSION_AUDIO off), re-sintetizamos pidiendo timestamps: el
  // alignment tiene que venir del mismo render del TTS que suena, porque los
  // tiempos de otra síntesis desincronizan el resaltado.
  const replayFetcher = async (): Promise<Blob> => {
    if (sessionId && replayTurnIndex != null) {
      try {
        const recorded = await fetchTurnAudioBlob(sessionId, replayTurnIndex, 'assistant', {
          retries: 3,
        });
        replayAlignRef.current = replayAlignment(alignment);
        return recorded;
      } catch {
        // Sin grabación del turno → resíntesis (abajo).
      }
    }
    const spoken = await speakTextWithAlignment(clean, sessionId ? { sessionId } : {});
    replayAlignRef.current = replayAlignment(spoken.startsMs);
    return spoken.blob;
  };

  const { wordSel, overlay: wordOverlay } = useBubbleWordLookup(cardRef, clean, {
    origin: 'coach',
    sessionId,
  });

  // Replay del TTS de la burbuja.
  const replay = useAudioBlob();

  // Karaoke del replay: mismo contrato que los getters del karaoke vivo, pero
  // contra el reloj del `<audio>` del blob en vez del player del WS.
  const replayPositionMs = replay.getPositionMs;
  const getReplayCharIndex = useCallback((): number => {
    const starts = replayAlignRef.current;
    if (!starts) return -1;
    const pos = replayPositionMs();
    if (pos < 0) return -1;
    return charIndexAt(starts, pos);
  }, [replayPositionMs]);
  const replaying = replay.state === 'playing' && replayAlignRef.current !== null;

  // Traducción por-burbuja on-demand (Bloque D): solo cuando el global está off.
  const [localTranslation, setLocalTranslation] = useState<string | null>(null);
  const [localTransLoading, setLocalTransLoading] = useState(false);
  const localActive = localTranslation !== null;

  const toggleBubbleTranslation = (): void => {
    if (localActive) {
      setLocalTranslation(null);
      return;
    }
    setLocalTransLoading(true);
    void translateSentence({ text: clean, ...(sessionId ? { sessionId } : {}) })
      .then((r) => setLocalTranslation(r.translation))
      .catch(() => setLocalTranslation('No pudimos traducir esta frase.'))
      .finally(() => setLocalTransLoading(false));
  };

  const shownTranslation = translation ?? localTranslation;
  const showTranslateBtn = !streaming && !translationEnabled;

  // Perf: las burbujas cerradas fuera del viewport no pagan layout/paint
  // (content-visibility, clase conv-cv). Se DESACTIVA con el popover de
  // palabra abierto: el paint containment recortaría el popover, que
  // desborda los límites de la burbuja.
  // La línea grande de la asistida tampoco: siempre está a la vista, y al cerrarse
  // su placeholder de 140 px achicaba el hilo un frame y lo soltaba del fondo.
  const cv = !streaming && !wordSel.sel && !featured;

  return (
    <div
      style={{ display: 'flex', gap: 6, alignItems: 'stretch', maxWidth: featured ? '100%' : '78%' }}
      className={[cv ? 'conv-cv' : '', animate ? 'slide-up' : ''].filter(Boolean).join(' ') || undefined}
    >
      {showBrush && !featured && (
        <div style={{ paddingTop: 8, marginRight: -4 }}>
          <BrushStroke color="var(--lime)" height={showBrush === 'long' ? 110 : 70} />
        </div>
      )}
      <div
        className={`card coach ${audioOff ? 'mode-text-out' : ''} ${consecutive ? 'is-consecutive' : ''} ${featured ? 'is-featured' : ''}`}
        style={{ flex: 1 }}
        ref={cardRef}
      >
        {/* Mientras streamea, el waveform ANIMA (coach-wave is-live) — señal
            de "hablando" sin tag textual redundante con el botón de stop. */}
        <div className="coach-head" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={`coach-wave ${streaming ? 'is-live' : ''}`} aria-hidden="true">
            <Waveform color="var(--lime)" size={20} />
          </span>
          <span className="tag" style={{ color: 'var(--lime)' }}>
            COACH
          </span>
        </div>
        <div className="body">
          <SpokenBody
            text={clean}
            boldRanges={bold}
            streaming={streaming}
            replaying={replaying}
            getRenderEndIdx={streaming ? getRenderEndIdx : FULL_REVEAL}
            getCharIndex={streaming ? getCharIndex : getReplayCharIndex}
            tappable={tappable}
            selRange={wordSel.sel ? [wordSel.sel.start, wordSel.sel.end] : null}
            onWordTap={wordSel.handleWordTap}
            challengeBlock={challengeBlock}
          />
          {gameChallenge && placement?.kind === 'below' && (
            <span
              className="game-cblock is-below"
              role="group"
              aria-label={GAME_ROUND2_COPY.blockAria(gameChallenge.tag.round)}
              style={translateItColorStyle()}
            >
              {gameChallenge.head}
              <span className="game-cblock__text" lang="es">
                {gameChallenge.tag.challenge}
              </span>
            </span>
          )}
        </div>

        {shownTranslation && <div className="coach-translation">{shownTranslation}</div>}
        {localTransLoading && (
          <div className="coach-translation" style={{ opacity: 0.6 }}>
            Traduciendo<span className="dots-anim" />
          </div>
        )}

        {/* Burbuja viva con voz: botón de stop para interrumpir al coach. */}
        {streaming && !audioOff && onInterrupt && (
          <div className="bubble-actions">
            <EscucharBtn playing onToggle={onInterrupt} />
          </div>
        )}

        {!streaming && (
          <div className="bubble-actions">
            {!audioOff && (
              <EscucharBtn
                playing={replay.state === 'playing'}
                onToggle={() => void replay.toggle(replayFetcher)}
              />
            )}
            {showTranslateBtn && <TranslateBubbleBtn active={localActive} onClick={toggleBubbleTranslation} />}
            <ReportAiReply
              surface={reportSurface}
              text={clean}
              sessionId={sessionId}
              turnIndex={reportTurnIndex ?? replayTurnIndex}
            />
          </div>
        )}

        {wordOverlay}
      </div>
    </div>
  );
}

